import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarOff,
  CalendarX2,
  CheckCircle2,
  ClipboardList,
  Clock,
  Car,
  ListFilter,
  MapPin,
  MapPinOff,
  Plane,
  RefreshCw,
  Search,
  Trash2,
  User,
} from 'lucide-react';
import type {
  AvailabilityBlocker,
  AvailabilityOpenSlot,
  RoutyAvailabilityAuditArtifact,
} from '../../business/routy/routyArtifacts';
import { formatDurationMinutes } from '../../utils/timeUtils';

interface AvailabilityAuditCardProps {
  artifact: RoutyAvailabilityAuditArtifact;
  disabled?: boolean;
  onRemove: (artifact: RoutyAvailabilityAuditArtifact, eventIds: string[]) => void;
  onKeep: (artifact: RoutyAvailabilityAuditArtifact) => void;
  onAuditSlot: (artifact: RoutyAvailabilityAuditArtifact, slot: AvailabilityOpenSlot) => void;
  /** Re-run the scheduling search on this request with the audited day preferred. */
  onRerunSlots?: (artifact: RoutyAvailabilityAuditArtifact) => void;
}

const OUTCOME_STYLES: Record<
  RoutyAvailabilityAuditArtifact['outcome'],
  { wrap: string; icon: React.ComponentType<{ className?: string }>; iconColor: string; text: string }
> = {
  dayOff: {
    wrap: 'bg-amber-50 border-amber-200',
    icon: CalendarOff,
    iconColor: 'text-amber-600',
    text: 'text-amber-900',
  },
  formRule: {
    wrap: 'bg-amber-50 border-amber-200',
    icon: ClipboardList,
    iconColor: 'text-amber-600',
    text: 'text-amber-900',
  },
  full: {
    wrap: 'bg-amber-50 border-amber-200',
    icon: CalendarX2,
    iconColor: 'text-amber-600',
    text: 'text-amber-900',
  },
  partial: {
    wrap: 'bg-ink-50 border-ink-200',
    icon: Clock,
    iconColor: 'text-ink-500',
    text: 'text-ink-800',
  },
};

const AvailabilityAuditCard: React.FC<AvailabilityAuditCardProps> = ({
  artifact,
  disabled,
  onRemove,
  onKeep,
  onAuditSlot,
  onRerunSlots,
}) => {
  const [resolved, setResolved] = useState(false);
  const [rerunSent, setRerunSent] = useState(false);
  // Address-less synced events are pre-selected: they're the ones that are
  // almost never real field work, so the common case is one tap to clear them.
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(artifact.blockers.filter((b) => b.removable && b.missingAddress && b.id).map((b) => b.id as string)),
  );
  const locked = disabled || resolved;

  const removable = useMemo(() => artifact.blockers.filter((b) => b.removable && b.id), [artifact.blockers]);
  // A closed day has no windows worth reasoning about — the schedule is the
  // answer, and showing "openings" there would contradict the verdict.
  const openSlots = artifact.outcome === 'dayOff' ? [] : artifact.openSlots ?? [];
  const closestMiss = artifact.outcome === 'dayOff' || openSlots.length > 0 ? null : artifact.closestMiss ?? null;
  // A day the availability schedule closes can't be opened by clearing
  // anything off it, so removal is never offered there — it would send the
  // operator down a path that changes nothing.
  const canClear = artifact.outcome !== 'dayOff' && artifact.outcome !== 'formRule' && removable.length > 0;
  const missingAddressCount = removable.filter((b) => b.missingAddress).length;
  const unlocatedCount = artifact.blockers.filter((b) => b.unlocatedAddress).length;
  const formHolds = artifact.outcome === 'formRule';

  const toggle = (id: string) => {
    if (locked) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const outcomeStyle = OUTCOME_STYLES[artifact.outcome];
  const VerdictIcon = outcomeStyle.icon;

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-ink-900">What's holding {artifact.dateLabel}</span>
          {artifact.forLabel && artifact.forLabel !== 'you' && (
            <span className="flex items-center gap-1 text-2xs font-medium text-ink-500">
              <User className="w-3 h-3" />
              {artifact.forLabel}
            </span>
          )}
        </div>
        {(artifact.requestedTimeLabel || artifact.earliestOfferedLabel) && (
          <p className="mt-0.5 text-2xs text-ink-500">
            {artifact.requestedTimeLabel ? `You wanted ${artifact.requestedTimeLabel}` : 'Any time that day'}
            {artifact.earliestOfferedLabel ? ` · booking offered ${artifact.earliestOfferedLabel}` : ''}
          </p>
        )}
      </div>

      <div className="p-3 space-y-2.5">
        <div className={`flex items-start gap-2.5 rounded-lg border p-3 ${outcomeStyle.wrap}`}>
          <VerdictIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${outcomeStyle.iconColor}`} />
          <div className="min-w-0">
            <p className={`text-sm font-semibold leading-snug ${outcomeStyle.text}`}>{artifact.verdict}</p>
            {artifact.outcome === 'dayOff' ? (
              <p className="mt-1 text-xs text-amber-800/90 leading-relaxed">
                This comes from the availability schedule, not from anything on the calendar — clearing events
                won't open the day. Turn the day back on from Availability in the sidebar.
              </p>
            ) : artifact.outcome === 'formRule' ? (
              <p className="mt-1 text-xs text-amber-800/90 leading-relaxed">
                The calendar can still have room — the booking engine skips this day because of a setting on{' '}
                {artifact.formRule?.name || 'this form'}. Change it from Booking Forms in the sidebar, or pick
                another day.
              </p>
            ) : (
              <>
                {artifact.workingHoursLabel && (
                  <p className="mt-1 text-xs text-ink-600">Working hours {artifact.workingHoursLabel}</p>
                )}
                {/* The number every opening below is measured against. Stated
                    once, up front, so each slot row can just say "short by 20m"
                    instead of restating the arithmetic four times. */}
                {artifact.serviceFit && (
                  <p className="mt-1 text-xs text-ink-600">
                    {artifact.serviceFit.name ? `${artifact.serviceFit.name} needs ` : 'This job needs '}
                    <span className="font-semibold text-ink-800">
                      {formatDurationMinutes(artifact.serviceFit.durationMinutes, { short: true })}
                    </span>
                    {artifact.serviceFit.bufferMinutes > 0 && (
                      <>
                        {' + '}
                        <span className="font-semibold text-ink-800">
                          {formatDurationMinutes(artifact.serviceFit.bufferMinutes, { short: true })}
                        </span>{' '}
                        buffer ={' '}
                        <span className="font-semibold text-ink-800">
                          {formatDurationMinutes(artifact.serviceFit.totalMinutes, { short: true })}
                        </span>
                      </>
                    )}
                    , before any driving.
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* The day is open, the job fits, and the form allows it. Left there,
            the card reads like the engine misbehaved — so this says what is
            actually going on: scheduling only offers a handful of ranked slots,
            and a day the request never asked for is not among them. */}
        {artifact.slotPreference && (
          <div className="flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <ListFilter className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-snug text-blue-900">
                Nothing is blocking this day — it wasn't asked for
              </p>
              <p className="mt-1 text-xs leading-relaxed text-blue-800/90">
                Scheduling doesn't offer every open window. It offers the Top Choice, a Proximity option, and
                times inside the dates the request lists as preferred
                {artifact.slotPreference.offeredLabel
                  ? `, which is why ${artifact.slotPreference.offeredLabel} came back instead`
                  : ''}
                {artifact.slotPreference.requestedDateLabel
                  ? `, and this request asks for ${artifact.slotPreference.requestedDateLabel}`
                  : ''}
                . {artifact.dateLabel} needs to be the preferred day for these windows to be offered.
              </p>
              {/* The whole fix is one re-run on the request this audit already
                  knows, so making the operator go redo the booking would be
                  handing back work we can do from here. */}
              {artifact.slotPreference.requestId && onRerunSlots && (
                <button
                  type="button"
                  onClick={() => {
                    setRerunSent(true);
                    onRerunSlots(artifact);
                  }}
                  disabled={disabled || rerunSent}
                  className="mt-2 flex items-center gap-1.5 rounded-md bg-blue-600 px-2.5 py-1.5 text-2xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RefreshCw className="h-3 w-3" />
                  Re-run times for {artifact.dateLabel}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Travel and all-day blocks are the answer when they apply, so they
            sit above the item list rather than being one row inside it. */}
        {artifact.travel?.exceedsWorkday && (
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <Plane className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-snug text-amber-900">
                On the road — about {artifact.travel.totalMiles.toLocaleString()} mi between stops
              </p>
              <p className="mt-1 text-xs leading-relaxed text-amber-800/90">
                That's roughly {formatDurationMinutes(artifact.travel.estimatedDriveMinutes)} of driving against
                a {formatDurationMinutes(artifact.travel.workingMinutes)} day
                {artifact.travel.longestLeg
                  ? `, including ${artifact.travel.longestLeg.miles.toLocaleString()} mi from "${artifact.travel.longestLeg.fromTitle}" to "${artifact.travel.longestLeg.toTitle}"`
                  : ''}
                . No job fits around that, so this day can't be booked at all — clearing events won't change it.
              </p>
              {artifact.travel.unmeasuredStops > 0 && (
                <p className="mt-1 text-xs leading-relaxed text-amber-800/90">
                  That total skips {artifact.travel.unmeasuredStops}{' '}
                  {artifact.travel.unmeasuredStops === 1 ? 'stop' : 'stops'} we couldn't locate, so the real
                  driving is worse.
                </p>
              )}
            </div>
          </div>
        )}

        {artifact.longBlocks?.map((b) => (
          <div
            key={b.id ?? b.title}
            className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-3"
          >
            <CalendarX2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-snug text-amber-900">
                {b.isAllDay ? 'All-day' : formatDurationMinutes(b.durationMinutes)} "{b.title}" block
              </p>
              <p className="mt-1 text-xs leading-relaxed text-amber-800/90">
                Routemize treats every synced event as busy time. To free this permanently, open it in
                {b.source ? ` ${b.source}` : ' your'} Calendar and switch it from Busy to Free.
              </p>
            </div>
          </div>
        ))}

        {artifact.blockedWindows?.map((w) => (
          <div key={`${w.label}-${w.reason}`} className="flex items-center gap-2 text-xs text-ink-600">
            <Clock className="w-3.5 h-3.5 flex-shrink-0 text-ink-400" />
            <span>
              {w.label} · <span className="text-ink-500">{w.reason}</span>
            </span>
          </div>
        ))}

        {/* The openings come before the inventory of events on purpose: which
            windows can take the job is the answer, while the list of everything
            on the day is only the supporting evidence. */}
        {openSlots.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-2xs font-medium uppercase tracking-wide text-ink-400">
              {formHolds
                ? `Calendar could take it (${openSlots.length})`
                : `Open ${openSlots.length === 1 ? 'window' : 'windows'} (${openSlots.length})`}
            </p>
            {openSlots.map((slot) => (
              <OpenSlotRow
                key={`${slot.startTime}-${slot.endTime}`}
                slot={slot}
                neededMinutes={artifact.serviceFit?.totalMinutes ?? null}
                disabled={!!disabled}
                onAudit={() => onAuditSlot(artifact, slot)}
              />
            ))}
          </div>
        )}

        {/* Nothing fits. Showing the near-miss as "the closest it gets" keeps
            the answer specific without dressing a gap the job can't use up as
            an opening — every Routemize booking has an address, so the drive
            around it is never optional. */}
        {closestMiss && (
          <div className="space-y-1.5">
            <p className="text-2xs font-medium uppercase tracking-wide text-ink-400">No openings</p>
            <div className="rounded-lg border border-ink-200 bg-surface p-2.5">
              <p className="text-xs leading-relaxed text-ink-600">
                Nothing on the day is long enough. The closest is{' '}
                <span className="font-semibold text-ink-900">
                  {closestMiss.startLabel} – {closestMiss.endLabel}
                </span>
                {closestMiss.bracketDriveMinutes != null && closestMiss.bracketDriveMinutes > 0 && (
                  <>
                    , and {formatDurationMinutes(closestMiss.bracketDriveMinutes, { short: true })} of it is
                    driving between those stops
                  </>
                )}
                {' — '}
                <span className="font-semibold text-ink-900">
                  {formatDurationMinutes(closestMiss.usableMinutes, { short: true })} usable
                </span>
                {artifact.serviceFit
                  ? ` against ${formatDurationMinutes(artifact.serviceFit.totalMinutes, { short: true })} needed.`
                  : '.'}
              </p>
              <button
                type="button"
                onClick={() => onAuditSlot(artifact, closestMiss)}
                disabled={disabled}
                className="mt-2 flex items-center gap-1.5 rounded-md border border-ink-200 bg-surface px-2.5 py-1 text-2xs font-semibold text-ink-600 transition-colors hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Search className="h-3 w-3" />
                Audit slot
              </button>
            </div>
          </div>
        )}

        {artifact.blockers.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-2xs font-medium uppercase tracking-wide text-ink-400">
              {artifact.workingHoursLabel ? 'Holding working hours' : 'On the day'} ({artifact.blockers.length})
            </p>
            {artifact.blockers.map((blocker, i) => (
              <BlockerRow
                key={blocker.id ?? `${blocker.title}-${i}`}
                blocker={blocker}
                selectable={canClear && blocker.removable && !!blocker.id}
                selected={!!blocker.id && selected.has(blocker.id)}
                locked={locked}
                onToggle={() => blocker.id && toggle(blocker.id)}
              />
            ))}
          </div>
        )}

        {/* Not offered for removal, deliberately — these are real stops with a
            sloppy address, so the fix is the address. What they need is to be
            called out, because they quietly skew every number above them. */}
        {unlocatedCount > 0 && (
          <p className="flex items-start gap-2 text-2xs text-amber-700 leading-relaxed">
            <MapPinOff className="w-3.5 h-3.5 mt-px flex-shrink-0" />
            {unlocatedCount === 1
              ? "1 event above has an address we can't locate, so it holds time but is left out of drive-time math."
              : `${unlocatedCount} events above have addresses we can't locate, so they hold time but are left out of drive-time math.`}{' '}
            Completing the location in the source calendar is what makes this day measurable.
          </p>
        )}

        {artifact.externalAvailability === 'displayOnly' && removable.length > 0 && (
          <p className="flex items-start gap-2 text-2xs text-ink-500 leading-relaxed">
            <AlertTriangle className="w-3.5 h-3.5 mt-px flex-shrink-0 text-ink-400" />
            Your synced calendars are set to display-only, so these events shouldn't be closing times off.
          </p>
        )}

        {canClear && (
          <div className="space-y-2 pt-0.5">
            {missingAddressCount > 0 && (
              <p className="text-xs text-ink-600 leading-relaxed">
                {missingAddressCount === 1
                  ? '1 synced event has no address'
                  : `${missingAddressCount} synced events have no address`}{' '}
                — usually a personal block rather than real work. Keep them, or clear them to free the time.
              </p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setResolved(true);
                  onRemove(artifact, [...selected]);
                }}
                disabled={locked || selected.size === 0}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Remove {selected.size > 0 ? selected.size : ''} and free the time
              </button>
              <button
                onClick={() => {
                  setResolved(true);
                  onKeep(artifact);
                }}
                disabled={locked}
                className="px-3 py-2 rounded-lg border border-ink-200 text-xs font-medium text-ink-600 hover:bg-ink-50 transition-colors disabled:opacity-50"
              >
                Keep all
              </button>
            </div>
            <p className="text-2xs text-ink-400 leading-relaxed">
              Removing takes the event off Routemize so it stops blocking availability. The original calendar
              event isn't touched.
            </p>
          </div>
        )}
      </div>

      {resolved && (
        <div className="px-3 pb-3 -mt-1 flex items-center justify-center gap-1.5 text-xs font-medium text-emerald-600">
          <CheckCircle2 className="w-4 h-4" />
          Sent to Routy
        </div>
      )}
    </div>
  );
};

interface OpenSlotRowProps {
  slot: AvailabilityOpenSlot;
  /** Visit + buffer the window has to hold, when the service is known. */
  neededMinutes: number | null;
  disabled: boolean;
  onAudit: () => void;
}

/**
 * One genuine opening, told as a sentence an operator can check: how long it
 * is, what it sits between, how much of it is already driving, and what that
 * leaves against the job. Only windows the job actually fits reach this row —
 * `fits` is null when no service was resolved to measure against. The "Audit
 * slot" button is the escape hatch to the full breakdown, so this stays a
 * summary.
 */
const OpenSlotRow: React.FC<OpenSlotRowProps> = ({ slot, neededMinutes, disabled, onAudit }) => {
  const fits = slot.fits;
  return (
    <div
      className={`rounded-lg border p-2.5 ${
        fits ? 'border-emerald-200 bg-emerald-50/40' : 'border-ink-200 bg-surface'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-ink-900">
          {slot.startLabel} – {slot.endLabel}
        </span>
        <span className="flex-shrink-0 text-xs font-semibold text-ink-700">
          {formatDurationMinutes(slot.durationMinutes, { short: true })}
        </span>
      </div>

      {(slot.afterTitle || slot.beforeTitle) && (
        <p className="mt-0.5 text-xs text-ink-500 truncate">
          {slot.afterTitle && `after ${slot.afterTitle}`}
          {slot.afterTitle && slot.beforeTitle && ' · '}
          {slot.beforeTitle && `before ${slot.beforeTitle}`}
        </p>
      )}

      {/* The drive is the part operators consistently forget, and it is why a
          window that "looks like an hour" is not an hour of work. */}
      {slot.bracketDriveMinutes != null && slot.bracketDriveMinutes > 0 && (
        <p className="mt-1 flex items-center gap-1.5 text-xs text-ink-600">
          <Car className="h-3.5 w-3.5 flex-shrink-0 text-ink-400" />
          {formatDurationMinutes(slot.bracketDriveMinutes, { short: true })} driving between those stops
          {slot.bracketDriveMiles != null ? ` · ${slot.bracketDriveMiles} mi` : ''} — leaves{' '}
          <span className="font-semibold text-ink-800">
            {formatDurationMinutes(slot.usableMinutes, { short: true })}
          </span>
        </p>
      )}

      {fits === true && (
        <p className="mt-1 text-xs font-medium text-emerald-700">
          Holds the job
          {neededMinutes != null
            ? ` — ${formatDurationMinutes(slot.usableMinutes, { short: true })} usable against ${formatDurationMinutes(neededMinutes, { short: true })} needed`
            : ''}
        </p>
      )}

      <button
        type="button"
        onClick={onAudit}
        disabled={disabled}
        className="mt-2 flex items-center gap-1.5 rounded-md border border-ink-200 bg-surface px-2.5 py-1 text-2xs font-semibold text-ink-600 transition-colors hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Search className="h-3 w-3" />
        Audit slot
      </button>
    </div>
  );
};

interface BlockerRowProps {
  blocker: AvailabilityBlocker;
  selectable: boolean;
  selected: boolean;
  locked: boolean;
  onToggle: () => void;
}

/**
 * Distance at which a stop stops reading as "a drive" and starts reading as
 * "you are not in town that day". Well past any plausible service radius, so
 * it only ever fires on genuine travel.
 */
const FAR_FROM_BASE_MILES = 100;

const BlockerRow: React.FC<BlockerRowProps> = ({ blocker, selectable, selected, locked, onToggle }) => {
  const isFarAway = (blocker.milesFromBase ?? 0) >= FAR_FROM_BASE_MILES;
  const body = (
    <>
      {selectable && (
        <span
          className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors ${
            selected ? 'border-blue-600 bg-blue-600 text-white' : 'border-ink-300 bg-surface'
          }`}
        >
          {selected && <CheckCircle2 className="h-3 w-3" />}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-ink-900 truncate">{blocker.title}</span>
          {blocker.kind === 'external' && (
            <span className="flex-shrink-0 rounded-full bg-ink-100 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-ink-500">
              {blocker.source || 'Synced'}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-ink-500">
          {blocker.startTime} – {blocker.endTime}
          {blocker.durationMinutes > 0 ? ` · ${formatDurationMinutes(blocker.durationMinutes, { short: true })}` : ''}
        </p>
        {blocker.missingAddress ? (
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-amber-700">
            <MapPinOff className="h-3.5 w-3.5 flex-shrink-0" />
            No address
          </p>
        ) : (
          <p
            className={`mt-0.5 flex items-center gap-1.5 text-xs ${
              isFarAway || blocker.unlocatedAddress ? 'text-amber-700' : 'text-ink-500'
            }`}
          >
            {blocker.unlocatedAddress ? (
              <MapPinOff className="h-3.5 w-3.5 flex-shrink-0" />
            ) : (
              <MapPin className={`h-3.5 w-3.5 flex-shrink-0 ${isFarAway ? '' : 'text-ink-400'}`} />
            )}
            <span className="truncate">{blocker.address}</span>
          </p>
        )}
        {/* An address that never resolved is the one failure that looks like
            nothing is wrong: the row reads complete, while the stop is absent
            from every distance the audit just computed. */}
        {blocker.unlocatedAddress && (
          <p className="mt-0.5 text-xs text-amber-700">
            Can&apos;t locate this address
            {blocker.addressGaps ? ` — no ${blocker.addressGaps}` : ''} · left out of drive-time math
          </p>
        )}
        {/* The distance is what turns "a 1-hour event" into "you're two states
            away" — the thing clock-only reasoning misses entirely. */}
        {isFarAway && (
          <p className="mt-0.5 text-xs font-medium text-amber-700">
            {blocker.milesFromBase!.toLocaleString()} mi from your starting point
          </p>
        )}
      </div>
    </>
  );

  if (!selectable) {
    return <div className="flex items-start gap-2.5 rounded-lg border border-ink-200 bg-surface p-2.5">{body}</div>;
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={locked}
      className={`w-full flex items-start gap-2.5 rounded-lg border p-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
        selected ? 'border-blue-300 bg-blue-50/40' : 'border-ink-200 bg-surface hover:border-ink-300 hover:bg-ink-50'
      }`}
    >
      {body}
    </button>
  );
};

export default AvailabilityAuditCard;
